// ── Enregistrement des routes ───────────────────────────────────────────
// Migration S5.4 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// S7 (mai 2026) : passage à `defineRoute` typé (cf. router.ts > Routes).
//
// Tous les `defineRoute(...)` sont ici pour garantir que TOUS les modules
// pages.X aient été évalués avant qu'on enregistre les handlers. boot est
// chargé en dernier dans main.ts, donc à ce moment tous les `pages.X =
// definePage(...)` sont définis et leurs valeurs sont stables.

import { defineRoute } from '../router.js';
import type { Routes } from '../router.js';
import { invokePage, type PagesTable } from './page-invoke.js';
import { initWorkspacePanel } from './workspace-panel.js';

// route() : helper local qui dispatche vers `pages.<key>` si défini, sinon
// no-op. Utile car defineRoute attend un handler typé selon le pattern, mais
// le mapping `pattern → pages.<key>` n'est pas inférable statiquement (ex.
// `'matrix/:pid'` et `'matrix/project/:pid'` partagent `pages.matrixView`).
// Le cast ici est borné par le type `Routes[P]` côté defineRoute, donc TS
// vérifie quand même le pattern.
function route<P extends keyof Routes>(pattern: P, pageKey: string, perm?: string): void {
  defineRoute(
    pattern,
    (params) => {
      // PageHandler attend Record<string,string> (rétrocompat). Comme params
      // est déjà coercé par defineRoute, on cast pour le passer tel quel
      // — handler est en fait un wrapper definePage qui re-coerce ; sans
      // intérêt mais inoffensif (et garde la rétrocompat pour les pages
      // qui n'ont pas migré vers definePage).
      //
      // `invokePage` RETOURNE la promesse ; cf. son module pour pourquoi ce
      // détail a sa place ailleurs qu'ici, et son test.
      return invokePage(pages as PagesTable, pageKey, params);
    },
    perm,
  );
}

route('dashboard', 'dashboard', 'projects.read');
// Workspace : pas de garde de permission sur la route — le catalogue est
// filtré par les droits de menu, et un utilisateur sans aucune tuile obtient
// une page d'amorçage, pas un refus. Consulter son propre espace de travail
// n'est jamais interdit.
route('workspace', 'workspace');
// Matrice de risques : gardée par risk_lists.read (visualisation des risques,
// hors Essentiel) et non plus projects.read. La chaîne de perm d'une route est
// en OR — on met risk_lists.read SEUL pour que programs.read ne contourne pas
// la garde risque sur la variante programme.
route('matrix/:pid', 'matrixView', 'risk_lists.read');
route('matrix/project/:pid', 'matrixView', 'risk_lists.read');
route('matrix/program/:gid', 'matrixViewProgram', 'risk_lists.read');
route('meetings-records', 'meetingsRecords', 'meetings_records.read');
route('meeting/:id', 'meeting', 'meetings_records.read');
route('postits', 'postits', 'postits.read');
route('project/:id', 'project', 'projects.read');
route('project/:id/revisions', 'projectRevisions', 'risk_lists.read');
route('program/:id', 'program', 'programs.read');
route('revision/:id', 'revision', 'risk_lists.read');
route('actions', 'actions', 'actions.read');
route('search', 'search', 'actions.read');
route('action/:id', 'action', 'actions.read');
route('action/:id/versions', 'actionVersions', 'actions.read');
route('action-plans', 'actionPlans', 'action_plans.read');
route('action-plan/:id', 'actionPlan', 'action_plans.read');
route('action-plan/:id/phase/:phaseId/kpi', 'actionPlanPhaseKpi', 'action_plans.read');
// Revue de backlog (août 2026) : liste des revues et écran d'arbitrage. La
// permission garde la route ET l'entrée de sidebar (data-perm).
route('reviews', 'backlogReviews', 'actions.review');
route('review/:id', 'backlogReview', 'actions.review');
route('review/:id/support', 'backlogReviewSupport', 'actions.review');
route('bugs', 'bugs', 'bugs.read');
route('bug/:id', 'bugDetail', 'bugs.read');
route('actions/merge/:keeperId/:absorbedId', 'actionsMerge', 'actions.merge');
route('notifications', 'notifications');
route('meeting-support', 'meetingSupport', 'meeting_support.generate');
route('invite-committee', 'inviteCommittee', 'meeting_support.generate');
// Évolution des risques (graphe) : même garde que la matrice — risk_lists.read.
route('evolution/:pid', 'evolutionView', 'risk_lists.read');
route('evolution/project/:pid', 'evolutionView', 'risk_lists.read');
route('evolution/program/:gid', 'evolutionViewProgram', 'risk_lists.read');
route('admin/committees', 'adminCommittees', 'committees.read');
// Fiche d'un comité : identité, relances et guides de réunion applicables.
// Remplace la modale d'édition (août 2026) — la config a dépassé la popup.
route('admin/committees/:id', 'adminCommitteeDetail', 'committees.read');
// Référentiel des guides de réunion : lecture ouverte comme les autres
// référentiels UI, l'écriture est gardée côté API par meeting_guides.write.
route('admin/meeting-guides', 'adminMeetingGuides', 'meeting_guides.read');
route('admin/generic-roles', 'adminGenericRoles', 'generic_roles.read');
route('admin/checklists', 'adminChecklists', 'checklists.read');
route('admin/checklists/:id', 'adminChecklistDetail', 'checklists.read');
route('bascule/:appId', 'bascule', 'checklists.read');
route('admin/risk-lists', 'adminRiskLists', 'risk_lists.read');
route('admin/risk-lists/:id', 'adminRiskListDetail', 'risk_lists.read');
route('admin/stages', 'adminStages', 'stages.read');
route('admin/action-statuses', 'adminActionStatuses', 'action_statuses.read');
route('admin/risk-colors', 'adminRiskColors', 'risk_colors.read');
route('admin/users', 'adminUsers', 'users.read');
route('admin/structures', 'adminStructures', 'structures.read');
route('admin/user-groups', 'adminUserGroups', 'user_groups.read');
route('admin/access-matrix', 'adminAccessMatrix', 'access_matrix.read');
route('admin/audit-log', 'adminAuditLog', 'audit_log.read');
route('admin/invitation-points', 'adminInvitationPoints', 'invitation_points.write');
route('admin/vocabulary', 'adminVocabulary', 'vocabulary.write');
route('admin/tenant-settings', 'adminTenantSettings', 'tenant_settings.read');
route('admin/mascot', 'adminMascot', 'mascot.write');
route('programs', 'programs', 'programs.read');
route('user-replacement', 'userReplacement', 'users.replace');
route('stats/usage', 'statsUsage', 'statistics_usage.read');
route('stats/project-synthesis', 'statsProjectSynthesis', 'statistics_synthesis.read');
// « Mes quotas » (conso IA) : rattaché au droit Analyse IA (meetings.analyze)
// plutôt que laissé sans garde. Hors Essentiel (palier sans IA).
route('my-quotas', 'myQuotas', 'meetings.analyze');
// « Mon compte » : aucune permission. Tout utilisateur connecté règle ses
// préférences, et le pied des mails groupés renvoie ici.
route('my-account', 'myAccount');

interface BootProjectState {
  dirtyRisks?: Set<unknown>;
  newActions?: Array<unknown>;
  dirtyActions?: Set<unknown>;
  newLinks?: Array<unknown>;
  deletedActionIds?: Array<unknown>;
}

// Bandeau rouge « période d'évaluation » (posé par pmo-admin via tenant_settings).
// Affiché en haut de la zone principale après connexion si is_evaluation=true.
// Fermable (✕) ; la fermeture est mémorisée en localStorage par date de clôture
// (si pmo-admin change la date, le bandeau réapparaît).
function renderEvaluationBanner(): void {
  const u = window.__authUser;
  if (!u || !u.is_evaluation) return;
  const main = document.getElementById('main');
  if (!main || document.getElementById('evaluation-banner')) return;
  const closure = u.application_closure_date || null;
  const dismissKey = `ornexia:eval-banner-dismissed:${closure || 'na'}`;
  try {
    if (tlsGet(dismissKey) === '1') return;
  } catch {
    /* localStorage indisponible : on affiche le bandeau quand même */
  }
  const msg = closure
    ? t('banner.evaluation', { date: fmtDate(closure) })
    : t('banner.evaluationNoDate');

  const banner = document.createElement('div');
  banner.id = 'evaluation-banner';
  banner.setAttribute('role', 'status');
  banner.style.cssText =
    'display:flex;align-items:center;gap:12px;background:#dc2626;color:#fff;padding:8px 16px;font-size:13px;font-weight:600;line-height:1.4';

  const span = document.createElement('span');
  span.style.cssText = 'flex:1';
  span.textContent = msg;

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', t('common.close'));
  closeBtn.textContent = '✕';
  closeBtn.style.cssText =
    'background:transparent;border:none;color:#fff;font-size:16px;cursor:pointer;padding:0 4px;line-height:1';
  closeBtn.addEventListener('click', () => {
    banner.remove();
    try {
      tlsSet(dismissKey, '1');
    } catch {
      /* noop */
    }
  });

  banner.appendChild(span);
  banner.appendChild(closeBtn);
  main.insertBefore(banner, main.firstChild);
}

document.addEventListener('DOMContentLoaded', () => {
  void (async () => {
    try {
      if (window.parent && window.parent !== window) {
        document.body.classList.add('embed-mode');
        const css = document.createElement('style');
        css.id = 'embed-mode-css';
        css.textContent = `
          body.embed-mode aside#sidebar,
          body.embed-mode #sidebar-overlay,
          body.embed-mode #page-header { display: none !important; }
          body.embed-mode main#main { margin-left: 0 !important; padding: 4px !important; }
          body.embed-mode #content { padding: 0 !important; margin: 0 !important; }
        `;
        document.head.appendChild(css);
      }
    } catch {
      /* noop */
    }

    document.querySelector('nav')?.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest<HTMLAnchorElement>('a[href^="#"]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (href && href.startsWith('#revision/')) return;
      const psState = ps as BootProjectState;
      if (
        psState.dirtyRisks?.size ||
        psState.newActions?.length ||
        psState.dirtyActions?.size ||
        psState.newLinks?.length ||
        psState.deletedActionIds?.length
      ) {
        e.preventDefault();
        void (async () => {
          const ok = await _confirmLeave();
          if (ok) {
            _removeUnloadGuard();
            if (href) window.location.hash = href.slice(1);
          }
        })();
        return;
      }
      // Garde générique des autres pages plein écran (édition projet/programme/
      // action, notes de réunion, remplacement utilisateur).
      if (hasUnsavedGuard()) {
        e.preventDefault();
        void (async () => {
          const ok = await confirmLeaveIfDirty();
          if (ok && href) window.location.hash = href.slice(1);
        })();
      }
    });

    try {
      if (window.__authUser && window.__authUser.id) {
        try {
          sessionStorage.setItem('currentUserId', String(window.__authUser.id));
        } catch (e: unknown) {
          safeWarn('sessionStorage.setItem currentUserId (boot)', e);
        }
      }
    } catch (e: unknown) {
      safeWarn('renderHeaderUserName (boot)', e);
    }

    const userLocale =
      (window.__authUser && (window.__authUser as { locale?: string }).locale) || undefined;
    await Promise.all([
      loadI18n(userLocale).catch((e: unknown) => safeWarn('loadI18n (boot)', e)),
      loadRiskColors().catch((e: unknown) => safeWarn('loadRiskColors (boot)', e)),
    ]);

    try {
      applyI18nDom();
    } catch (e: unknown) {
      safeWarn('applyI18nDom (boot)', e);
    }

    document.getElementById('header-account-btn')?.addEventListener('click', toggleAccountMenu);
    // Assistant d'aide, Signaler un bug et À propos sont désormais dans le menu
    // du compte (refonte header 2026-07) : leur câblage vit dans toggleAccountMenu().

    void renderHeaderActionCapture();
    window.addEventListener('auth:ready', () => void renderHeaderActionCapture());

    window.refreshNotifBadge = async function refreshNotifBadge(): Promise<void> {
      const el = document.getElementById('nav-notifications-badge');
      if (!el) return;
      try {
        const res = (await api.getNotificationsBadge()) as { count?: number };
        const n = Number(res?.count) || 0;
        if (n > 0) {
          el.textContent = n > 99 ? '99+' : String(n);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      } catch {
        /* silencieux */
      }
    };
    window.addEventListener('auth:ready', () => void window.refreshNotifBadge?.());
    window.addEventListener('auth:ready', renderEvaluationBanner);
    if (window.__authUser?.id) renderEvaluationBanner();
    window.addEventListener('hashchange', () => void window.refreshNotifBadge?.());
    setInterval(() => void window.refreshNotifBadge?.(), 60000);
    if (window.__authUser?.id) void window.refreshNotifBadge?.();

    window.refreshBugsBadge = async function refreshBugsBadge(): Promise<void> {
      // Le picto Bug est passé dans le menu du compte (refonte header 2026-07).
      // On garde ici l'indicateur « réponses non lues » sous forme d'un point
      // discret sur le picto Profil ; le compteur exact est exposé via
      // window.__bugsBadgeCount et affiché dans le menu, en face de « Signaler un bug ».
      const dot = document.getElementById('header-account-bug-dot');
      // Même garde que la route #bugs et l'endpoint /bugs/badge-count : le badge
      // ne concerne que les utilisateurs ayant accès au suivi des bugs. Sans ce
      // filtre, un compte sans `bugs.read` (ex. admin technique DMO) déclenche un
      // 403 réseau à chaque page — bruit serveur + échec du smoke e2e.
      if (!window.hasPermission?.('bugs.read')) {
        window.__bugsBadgeCount = 0;
        if (dot) dot.style.display = 'none';
        return;
      }
      try {
        const res = (await api.getBugsBadge()) as { count?: number };
        const n = Number(res?.count) || 0;
        window.__bugsBadgeCount = n;
        if (dot) dot.style.display = n > 0 ? 'block' : 'none';
      } catch {
        /* silencieux */
      }
    };
    window.addEventListener('auth:ready', () => void window.refreshBugsBadge?.());
    window.addEventListener('hashchange', () => void window.refreshBugsBadge?.());
    setInterval(() => void window.refreshBugsBadge?.(), 60000);
    if (window.__authUser?.id) void window.refreshBugsBadge?.();

    // Volet des tuiles : le préchargement du store se fait ici, pour que
    // l'ouverture du volet ne coûte aucune requête — c'est ce qui remplace
    // le fait de le garder collant.
    initWorkspacePanel();

    router.init();
  })();
});

export {};
